import type { TFunction } from 'i18next'

import { UNIT_KINDS, unitLabel, unitObject } from '@/lib/legUnits'
import type { UnitKind } from '@/types/domain'

import type { UnitRow } from './legRows'

/* Which unit cards a leg page shows, and what each card asks (S-18b).
 *
 * ── The mode decides the defaults, never the data ───────────────────────────
 *
 * A road leg is trucks, a sea leg containers and bills of lading, a rail leg
 * wagons and containers, an air leg air waybills. The leg page opens the cards
 * its mode implies (`defaultUnitKinds`) — and a kind that already has a unit on
 * the leg, saved or typed, is shown whatever the mode says, so changing the
 * mode never hides a unit. «+ Другой вид единицы» adds any other kind's card.
 *
 * The code read is the CATALOGUE code: the draft's chosen mode row (so a new
 * leg switched to «Море» switches its cards before the first save), falling
 * back to the served `transportModeCode`. Never `CaseLeg.mode`: that is the
 * lossy display value, which folds every unknown code into 'multimodal'. Both
 * multimodal spellings, an empty and an unknown code show every kind (owner).
 *
 * ── The fields per kind are the server's (`casefile.unitProblems`) ──────────
 *
 * A non-empty field not listed for the row's kind is refused 422
 * `units.<i>.<field>: not-for-kind`, so a card asks only its kind's fields and
 * the payload sends only them. Every kind has its own carrier.
 */

/** A unit's optional facts beside its number and carrier. */
export type UnitField =
  | 'trailerPlate'
  | 'containerNumber'
  | 'cmrNumber'
  | 'sealNumber'
  | 'unitType'
  | 'driverName'
  | 'driverPhone'
  | 'weightKg'

/** The fields each kind carries — the Locked table, mirrored from
 *  `casefile.unitFields`. A wagon's seal is its ЗПУ (owner), optional. */
export const UNIT_FIELDS: Record<UnitKind, readonly UnitField[]> = {
  truck: ['trailerPlate', 'containerNumber', 'cmrNumber', 'driverName', 'driverPhone', 'weightKg'],
  container: ['sealNumber', 'unitType', 'weightKg'],
  wagon: ['sealNumber', 'unitType', 'weightKg'],
  awb: ['weightKg'],
  bl: [],
  other: ['weightKg'],
}

/** Whether a kind carries a field. */
export function carries(kind: UnitKind, field: UnitField): boolean {
  return UNIT_FIELDS[kind].includes(field)
}

/** A column of a unit card's desk grid. */
export type UnitColumn =
  | 'number'
  | 'trailer'
  | 'seal'
  | 'unitType'
  | 'driver'
  | 'phone'
  | 'carrier'
  | 'weight'
  | 'rate'
  | 'remaining'

/** The columns a kind's card draws, in order, before the actions. A truck's
 *  CMR and container are one press away (the row's chevron), not columns: two
 *  more inputs per row is the ninth and tenth column on a desk that mostly
 *  leaves them blank. */
export const UNIT_COLUMNS: Record<UnitKind, readonly UnitColumn[]> = {
  truck: ['number', 'trailer', 'driver', 'phone', 'carrier', 'weight', 'rate', 'remaining'],
  container: ['number', 'seal', 'unitType', 'carrier', 'weight', 'rate', 'remaining'],
  wagon: ['number', 'seal', 'unitType', 'carrier', 'weight', 'rate', 'remaining'],
  awb: ['number', 'carrier', 'weight', 'rate', 'remaining'],
  bl: ['number', 'carrier', 'rate', 'remaining'],
  other: ['number', 'carrier', 'weight', 'rate', 'remaining'],
}

/** The columns that exist only for a reader shown agent identity (AD-6). */
export const IDENTITY_COLUMNS: ReadonlySet<UnitColumn> = new Set(['driver', 'phone', 'carrier'])

/** The fields behind a row's chevron — a truck's only. */
export function foldedFields(kind: UnitKind): readonly ('cmrNumber' | 'containerNumber')[] {
  return kind === 'truck' ? ['cmrNumber', 'containerNumber'] : []
}

/** The cards a leg of this mode opens with. The code is folded to upper case
 *  (fixtures and desk-added catalogue rows disagree about case); `MULTI`,
 *  `MULTIMODAL`, an empty and an unknown code show every kind. */
export function defaultUnitKinds(code: string | undefined): UnitKind[] {
  switch ((code ?? '').trim().toUpperCase()) {
    case 'ROAD':
      return ['truck']
    case 'SEA':
      return ['container', 'bl']
    case 'RAIL':
      return ['wagon', 'container']
    case 'AIR':
      return ['awb']
    default:
      return [...UNIT_KINDS]
  }
}

/** The cards a leg page draws: the mode's defaults in their order, then every
 *  other kind that holds a unit (`present`) or that the reader added through
 *  «+ Другой вид единицы» (`added`), in `UNIT_KINDS` order. */
export function shownUnitKinds({
  code,
  present,
  added,
}: {
  code: string | undefined
  present: Iterable<UnitKind>
  added: Iterable<UnitKind>
}): UnitKind[] {
  const defaults = defaultUnitKinds(code)
  const more = new Set<UnitKind>([...present, ...added])
  return [...defaults, ...UNIT_KINDS.filter((kind) => more.has(kind) && !defaults.includes(kind))]
}

/* ── Words ─────────────────────────────────────────────────────────────── */

/** A column's header word — the number column is the kind's own («Госномер»,
 *  «№ контейнера»), the seal on a wagon is its ЗПУ. */
export function columnLabel(t: TFunction, kind: UnitKind, column: UnitColumn | 'cmr' | 'container'): string {
  switch (column) {
    case 'number':
      return t(`cases.legs.units.number.${kind}` as 'cases.legs.units.number.truck')
    case 'seal':
      return kind === 'wagon' ? t('cases.legs.units.columns.zpu') : t('cases.legs.units.columns.seal')
    case 'unitType':
      return t('cases.legs.units.columns.unitType')
    case 'remaining':
      return t('cases.legs.units.columns.remaining')
    default:
      return t(`cases.legs.vehiclesTable.columns.${column}`)
  }
}

/** The columns a row of this kind draws for this reader. */
export function visibleColumns(kind: UnitKind, showIdentity: boolean): UnitColumn[] {
  return UNIT_COLUMNS[kind].filter((column) => showIdentity || !IDENTITY_COLUMNS.has(column))
}

/** «Госномер, строка 2» / «Пломба, контейнер 1» — unique across the page's
 *  cards, so two cards' «Ставка» boxes are two names. */
export function cellLabel(t: TFunction, kind: UnitKind, column: string, n: number): string {
  return t(`cases.legs.units.cellLabel.${kind}` as 'cases.legs.units.cellLabel.truck', { column, n })
}

/** The row named as a heading — «Контейнер MSKU…», or «Контейнер 2» while it
 *  has no number. */
export function rowTitle(t: TFunction, row: UnitRow, n: number): string {
  return row.number.trim()
    ? unitLabel(t, row)
    : t(`cases.legs.units.rowN.${row.kind}` as 'cases.legs.units.rowN.truck', { n })
}

/** The row as the object of an action — «фуру 01KZ…», «контейнер 2». */
export function rowObject(t: TFunction, row: UnitRow, n: number): string {
  return unitObject(t, { kind: row.kind, number: row.number.trim() || String(n) })
}

/** The column a served `units.<i>.<field>` names, so the refusal is worded
 *  under the field the reader typed (AC #14). `kind` — a kind the server does
 *  not know — is said against the number, the row's identity. */
export function serverFieldColumn(field: string): UnitColumn | 'cmr' | 'container' {
  switch (field) {
    case 'trailerPlate':
      return 'trailer'
    case 'containerNumber':
      return 'container'
    case 'cmrNumber':
      return 'cmr'
    case 'sealNumber':
      return 'seal'
    case 'unitType':
      return 'unitType'
    case 'carrierId':
    case 'carrierName':
      return 'carrier'
    case 'driverName':
      return 'driver'
    case 'driverPhone':
      return 'phone'
    case 'weightKg':
      return 'weight'
    default:
      return 'number'
  }
}
